import React, { useState, useRef, useEffect } from 'react';
import { buildApiUrl } from '../utils/apiUrl';
import trackVisitor from '../utils/visitorTracker';

const placeholderImg = '/defaultimages/imagenotavailable.jpg';

const Rentals = () => {
  // State management
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [location, setLocation] = useState('');
  const [type, setType] = useState('');
  const [modalItem, setModalItem] = useState(null);
  const [selectedModalImage, setSelectedModalImage] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  // Ref for horizontally scrolling featured row
  const featuredScrollRef = useRef(null);

  // Fetch rentals from API
  useEffect(() => {
    fetchRentals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRentals = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(buildApiUrl('/api/rentalsgeneral'));
      
      if (!response.ok) {
        throw new Error('Failed to fetch rentals');
      }
      
      const data = await response.json();
      setRentals(data);
    } catch (err) {
      console.error('Error fetching rentals:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const scrollFeaturedBy = (delta) => {
    if (featuredScrollRef.current) {
      featuredScrollRef.current.scrollBy({ left: delta, behavior: 'smooth' });
    }
  };

  // Modal handlers
  const handleViewDetails = (item) => {
    trackVisitor('rentals', item.bid, 'Rental listing clicked');
    setModalItem(item);
    setShowShareMenu(false);
    if (item.photos && Array.isArray(item.photos) && item.photos.length > 0) {
      setSelectedModalImage(item.photos[0]);
    } else {
      setSelectedModalImage(item.imagepath || placeholderImg);
    }
  };

  const closeModal = () => {
    setModalItem(null);
    setShowShareMenu(false);
    setSelectedModalImage(null);
  };

  // Extract location and type options from data
  const locationOptions = Array.from(new Set(
    rentals.map(r => r.locationname?.split(',')[1]?.trim() || r.locationname?.split(',')[0]?.trim())
  )).filter(Boolean).sort();
  
  const typeOptions = Array.from(new Set(rentals.map(r => r.businesstype))).filter(Boolean).sort();

  // Get name suggestions based on search input
  const suggestions = search.length > 0
    ? Array.from(new Set(
        rentals
          .filter(r => r.businessname && r.businessname.toLowerCase().includes(search.toLowerCase()))
          .map(r => r.businessname)
      )).slice(0, 8)
    : [];

  // Filter logic
  let filteredRentals = rentals;
  if (location !== '') {
    filteredRentals = filteredRentals.filter(r => 
      (r.locationname?.split(',')[1]?.trim() || r.locationname?.split(',')[0]?.trim()) === location
    );
  }
  if (type !== '') {
    filteredRentals = filteredRentals.filter(r => r.businesstype === type);
  }
  if (search.trim() !== '') {
    filteredRentals = filteredRentals.filter(r =>
      (r.businessname && r.businessname.toLowerCase().includes(search.trim().toLowerCase())) ||
      (r.businesstype && r.businesstype.toLowerCase().includes(search.trim().toLowerCase())) ||
      (r.title && r.title.toLowerCase().includes(search.trim().toLowerCase())) ||
      (r.aboutdescription && r.aboutdescription.toLowerCase().includes(search.trim().toLowerCase()))
    );
  }

  // Featured rentals
  const featuredRentals = rentals.filter(r => r.featuredlisting === true || r.featuredlisting === 1);

  // Loading state
  if (loading) {
    return (
      <div style={{ width: '100%', maxWidth: '100vw', margin: 0, padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <div style={{ fontSize: 18, color: '#666' }}>Loading rentals...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ width: '100%', maxWidth: '100vw', margin: 0, padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16, color: '#d32f2f' }}>⚠️</div>
        <div style={{ fontSize: 18, marginBottom: 8, color: '#d32f2f' }}>Error loading rentals</div>
        <div style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>{error}</div>
        <button
          onClick={fetchRentals}
          style={{
            padding: '10px 20px',
            fontSize: 14,
            background: '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '100vw', margin: 0, padding: 10, boxSizing: 'border-box', overflowX: 'hidden' }}>
      {/* Header */}
      <header style={{ background: '#e8f5e9', color: '#388e3c', padding: '0.25rem', textAlign: 'center', borderRadius: 8, marginBottom: 2 }}>
        <h1 style={{ margin: 0, fontSize: '1.0rem', fontWeight: 700 }}>Rentals: Cars, Houses, Equipment & More</h1>
      </header>

      {/* Featured Section */}
      {featuredRentals.length > 0 && (
        <div style={{ marginBottom: '0.2rem', position: 'relative' }}>
          
          {/* Left scroll button */}
          <button
            aria-label="Scroll featured left"
            onClick={() => scrollFeaturedBy(-250)}
            style={{
              position: 'absolute',
              top: '35%',
              transform: 'translateY(-50%)',
              left: 0,
              zIndex: 2,
              background: 'rgba(56, 142, 60, 0.9)',
              color: '#fff',
              border: 'none',
              borderRadius: 20,
              width: 28,
              height: 28,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18
            }}
          >
            ‹
          </button>

          {/* Featured scroll container */}
          <div
            ref={featuredScrollRef}
            style={{
              display: 'flex',
              gap: 0,
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              padding: '4px 44px',
              scrollSnapType: 'x mandatory',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}
          >
            {featuredRentals.map((item) => (
              <div
                key={item.bid}
                onClick={() => handleViewDetails(item)}
                style={{
                  minWidth: 105,
                  maxWidth: 125,
                  height: 185,
                  background: '#fff',
                  border: '.5px solid #388e3c',
                  borderRadius: 12,
                  padding: 8,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  scrollSnapAlign: 'start',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                <img
                  src={item.imagepath || placeholderImg}
                  alt={item.businessname}
                  style={{ width: 110, height: 70, objectFit: 'cover', borderRadius: 8, marginBottom: 0 }}
                  onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
                />
                <h3 style={{ margin: 0, fontSize: 13, color: '#388e3c' }}>{item.businessname}</h3>
                {item.businesstype && <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>{item.businesstype}</div>}
                <div style={{ fontSize: 12, color: '#888', marginBottom: 2, fontWeight: 600 }}>
                  📍 {item.locationname} • {item.areaname}
                </div>
                {item.contactperson && (
                  <div style={{ fontSize: 12, color: '#777', marginBottom: 4, marginTop: '-2px' }}>
                    Contact: <span style={{ fontWeight: 600, color: '#555' }}>{item.contactperson}</span>
                  </div>
                )}
                
                {/* Social media icons */}
                {(item.sm_instagram || item.sm_facebook || item.sm_youtube) && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    {item.sm_instagram && (
                      <a
                        href={`https://instagram.com/${item.sm_instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 18,
                          height: 18,
                          borderRadius: 4,
                          background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="Instagram"
                      >
                        IG
                      </a>
                    )}
                    {item.sm_facebook && (
                      <a
                        href={`https://facebook.com/${item.sm_facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 18,
                          height: 18,
                          borderRadius: 4,
                          background: '#1877F2',
                          color: '#fff',
                          fontSize: 14,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="Facebook"
                      >
                        f
                      </a>
                    )}
                    {item.sm_youtube && (
                      <a
                        href={`https://youtube.com/${item.sm_youtube}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 18,
                          height: 18,
                          borderRadius: 4,
                          background: '#FF0000',
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="YouTube"
                      >
                        YT
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right scroll button */}
          <button
            aria-label="Scroll featured right"
            onClick={() => scrollFeaturedBy(250)}
            style={{
              position: 'absolute',
              top: '35%',
              transform: 'translateY(-50%)',
              right: 0,
              zIndex: 10,
              background: 'rgba(56, 142, 60, 0.95)',
              color: '#fff',
              border: 'none',
              borderRadius: 20,
              width: 28,
              height: 28,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          >
            ›
          </button>
        </div>
      )}

      {/* Filters Section */}
      <div style={{ background: '#f5f5f5', padding: '8px 12px 8px 12px', borderRadius: 8, marginBottom: 10 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: '-8px' }}>
          {/* Dropdowns */}
          <div style={{marginTop: '8px'}}>
            <select
              value={location}
              onChange={(e) => { setLocation(e.target.value); }}
              style={{
                width: '100%',
                padding: '4px 10px',
                fontSize: 12,
                border: '1px solid #c5e1a5',
                borderRadius: 6,
                outline: 'none',
                background: '#fff',
              }}
            >
              <option value="">All Locations</option>
              {locationOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '.5px', marginBottom: '2px', marginLeft: '2px' }}>
              {filteredRentals.length} result{filteredRentals.length !== 1 ? 's' : ''} found
            </div>
          </div>

          {/* Type filter */}
          <div style={{marginTop: '-4px'}}>
            <select
              value={type}
              onChange={(e) => { setType(e.target.value); }}
              style={{
                width: '100%',
                padding: '4px 10px',
                fontSize: 12,
                border: '1px solid #c5e1a5',
                borderRadius: 6,
                outline: 'none',
                background: '#fff',
                cursor: 'pointer'
              }}
            >
              <option value="">All Types</option>
              {typeOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          
        {/* Search with suggestions */}
        <div style={{ position: 'relative', marginTop: '-2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Business name, type, or description..."
            style={{
              width: '60%',
              padding: '4px 10px',
              fontSize: 12,
              border: '1px solid #c5e1a5',
              borderRadius: 6,
            }}
          />
          <button
            onClick={() => {
              setSearch('');
              setLocation('');
              setType('');
              setShowSuggestions(false);
            }}
            style={{
              padding: '4px 10px',
              background: '#3643f4ff',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 600,
              marginTop: '-2px'
            }}
          >
            Reset
          </button>
          {showSuggestions && (
            <ul style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              width: '60%',
              background: '#fff',
              border: '1px solid #ccc',
              borderRadius: 6,
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              listStyle: 'none',
              padding: 0,
              margin: 0,
              zIndex: 1000,
            }}>
              {suggestions.map((suggestion, index) => (
                <li
                  key={index}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: index !== suggestions.length - 1 ? '1px solid #eee' : 'none',
                  }}
                  onMouseDown={() => {
                    setSearch(suggestion);
                    setShowSuggestions(false);
                  }}
                >
                  {suggestion}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      </div>



      {/* Rentals Grid */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 310px))',
        gap: '0.5rem',
        marginTop: '-0.7rem',
        marginBottom: 20,
        justifyContent: 'center'
      }}>
        {filteredRentals.map((rental) => (
          <div
            key={rental.bid}
            onClick={() => handleViewDetails(rental)}
            style={{
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: 12,
              padding: 12,
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              height: '280px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)';
            }}
          >
            <img
              src={rental.imagepath || placeholderImg}
              alt={rental.businessname}
              style={{ width: 260, height: 135, objectFit: 'cover', borderRadius: 8, alignSelf: 'center' }}
              onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 2 }}>
                <h3 style={{ margin: 0, fontSize: 16, color: '#388e3c' }}>{rental.businessname}</h3>
                {rental.featuredlisting && (
                  <span style={{ 
                    background: '#388e3c',
                    color: '#fff',
                    padding: '2px 6px',
                    borderRadius: 3,
                    fontSize: 10,
                    fontWeight: 600,
                    marginLeft: 8,
                    flexShrink: 0
                  }}>
                    Featured
                  </span>
                )}
              </div>
              {rental.businesstype && <div style={{ fontSize: 13, color: '#666', marginBottom: 4, fontWeight: 500 }}>{rental.businesstype}</div>}
               <div style={{ fontSize: 12, color: '#888', marginBottom: 2, fontWeight: 600 }}>
                📍 {rental.locationname} • {rental.areaname}
              </div>
              {rental.price && (
                <div style={{ fontSize: 12, color: '#444', marginBottom: 2, marginTop: '-2px' }}>
                  <span style={{ fontWeight: 600 }}>price:</span> <span style={{ fontWeight: 400, color:'#388e3c' }}>{rental.price}</span>
                </div> 
              )}
              {rental.contactperson && (
                <div style={{ fontSize: 13, color: '#444', marginBottom: 4, marginTop: '-2px' }}>
                  Contact: <span style={{ fontWeight: 600 }}>{rental.contactperson}</span>
                </div>
              )}
             
              {/* Social media icons */}
              {(rental.sm_instagram || rental.sm_facebook || rental.sm_youtube) && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  {rental.sm_instagram && (
                    <a
                      href={`https://instagram.com/${rental.sm_instagram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: 5,
                        background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                        color: '#fff',
                        fontSize: 15,
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        fontFamily: 'Arial, sans-serif'
                      }}
                      title="Instagram"
                    >
                      IG
                    </a>
                  )}
                  {rental.sm_facebook && (
                    <a
                      href={`https://facebook.com/${rental.sm_facebook}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: 5,
                        background: '#1877F2',
                        color: '#fff',
                        fontSize: 16,
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        fontFamily: 'Arial, sans-serif'
                      }}
                      title="Facebook"
                    >
                      f
                    </a>
                  )}
                  {rental.sm_youtube && (
                    <a
                      href={`https://youtube.com/${rental.sm_youtube}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: 5,
                        background: '#FF0000',
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        fontFamily: 'Arial, sans-serif'
                      }}
                      title="YouTube"
                    >
                      ▶
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* No results message */}
      {filteredRentals.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <div style={{ fontSize: 18, marginBottom: 8 }}>No rentals found</div>
          <div style={{ fontSize: 14 }}>Try adjusting your filters or search terms</div>
        </div>
      )}

      {/* Modal */}
      {modalItem && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 16,
              maxWidth: 600,
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
            }}
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'rgba(0,0,0,0.5)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: 36,
                height: 36,
                fontSize: 24,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              ×
            </button>

            {/* Modal content: Preview and Thumbnails */}
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <img
                src={selectedModalImage || placeholderImg}
                alt={modalItem.businessname}
                style={{ width: '90%', maxWidth: 520, height: 240, objectFit: 'cover', borderRadius: 12, display: 'block', marginBottom: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 8 }}>
                {(modalItem.photos && Array.isArray(modalItem.photos) && modalItem.photos.length > 0
                  ? modalItem.photos
                  : [modalItem.imagepath || placeholderImg]
                ).map((imgUrl, idx) => (
                  <img
                    key={idx}
                    src={imgUrl || placeholderImg}
                    alt={modalItem.businessname + ' preview ' + (idx + 1)}
                    style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: 6, border: selectedModalImage === imgUrl ? '2px solid #1976d2' : '2px solid #eee', cursor: 'pointer', boxShadow: selectedModalImage === imgUrl ? '0 0 6px #1976d2' : 'none' }}
                    onClick={() => setSelectedModalImage(imgUrl)}
                    onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
                  />
                ))}
              </div>
            </div>
            
            <div style={{ padding: '8px 20px 20px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 2 }}>
                <h2 style={{ margin: 0, fontSize: 17, color: '#388e3c', flex: 1 }}>{modalItem.businessname}</h2>
                {modalItem.featuredlisting && (
                  <span style={{ 
                    background: '#388e3c',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    marginLeft: 12,
                    flexShrink: 0
                  }}>
                    Featured
                  </span>
                )}
              </div>
              
              {modalItem.businesstype && (
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6, fontWeight: 500 }}>{modalItem.businesstype}</div>
              )}
              <div style={{ fontSize: 14, color: '#888', marginBottom: 1, fontWeight: 600 }}>
                📍 {modalItem.locationname.trim()} • {modalItem.areaname}
              </div>
              <div style={{ marginTop: 4, marginBottom: 4 }}>
                  <strong style={{ fontSize: '0.90rem', color: '#333', display: 'inline' }}>Description:</strong>
                  <span style={{ marginLeft: 6, color: '#333', lineHeight: 1.4, fontSize: '0.90rem', display: 'inline' }}>{modalItem.aboutdescription}</span>
                </div>
              {modalItem.price && (
                  <div style={{ fontSize: 15, color: '#444', marginBottom: 4 }}>
                  Price: <span style={{ fontWeight: 600, color:'#388e3c' }}>{modalItem.price}</span>
                </div> 
              )}
              {modalItem.contactnumber && (
                <div style={{ marginBottom: 1, marginTop: 8 }}>
                  <strong style={{ fontSize: 16, color: '#333', marginBottom: 0, marginTop: 0 }}>Contact Details:</strong>
                  {modalItem.contactperson && (
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#666', marginTop: 4 }}>
                      Name: <span style={{ fontWeight: 500, color: '#555' }}>{modalItem.contactperson}</span>
                    </div>
                  )}
                  <a 
                    href={`tel:${modalItem.contactnumber.replace(/\s/g, '')}`}
                    style={{ 
                      fontSize: 15, 
                      color: '#d32f2f', 
                      marginTop: 4, 
                      fontWeight: 600,
                      textDecoration: 'none',
                      display: 'block',
                      cursor: 'pointer'
                    }}
                  >
                    📞 {modalItem.contactnumber}
                  </a>
                </div>
              )}

              {modalItem.emailid && (
                <div style={{ marginBottom: 2 }}>
                  <strong style={{ fontSize: 14, color: '#333' }}>Email:</strong>
                  <a href={`mailto:${modalItem.emailid}`} style={{ fontSize: 14, color: '#1976d2', textDecoration: 'none', marginLeft: 8 }}>{modalItem.emailid}</a>
                </div>
              )}

              {modalItem.address1 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 14, color: '#333' }}>Address:</strong>
                    <span style={{ fontSize: 14, color: '#666' }}>{modalItem.address1}</span>
                  </div>
                </div>
              )}

              {/* Call Now and Share buttons */}
              <div style={{ display: 'flex', gap: 12, marginTop: 20, marginBottom: 8, justifyContent: 'center' }}>
                {/* Call Now button */}
                {modalItem.contactnumber && (
                  <button
                    onClick={() => window.location.href = `tel:${modalItem.contactnumber.replace(/\s/g, '')}`}
                    style={{
                      padding: '8px 16px',
                      background: '#4CAF50',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 5,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#45a049'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#4CAF50'}
                  >
                    📞 Call
                  </button>
                )}
                
                {/* Share button with dropdown */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    style={{
                      padding: '8px 16px',
                      background: '#1976d2',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 5,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#1565c0'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#1976d2'}
                  >
                    � Share
                  </button>
                  
                  {/* Share dropdown menu */}
                  {showShareMenu && (
                    <div style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: 0,
                      right: 0,
                      marginBottom: 8,
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 100,
                      overflow: 'hidden'
                    }}>
                      {/* WhatsApp option (default) */}
                      <button
                        onClick={() => {
                          const shareText = `*${modalItem.businessname}*\n${modalItem.businesstype || ''}\n📍 ${modalItem.locationname || ''}\n📞 ${modalItem.contactnumber || ''}${modalItem.emailid ? `\n✉️ ${modalItem.emailid}` : ''}`;
                          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
                          window.open(whatsappUrl, '_blank');
                          setShowShareMenu(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          background: '#25D366',
                          color: '#fff',
                          border: 'none',
                          borderBottom: '1px solid #eee',
                          fontSize: 24,
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#20ba5a'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#25D366'}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                      </button>
                      
                      {/* Email option */}
                      <button
                        onClick={() => {
                          const subject = encodeURIComponent(`Check out ${modalItem.businessname}`);
                          const body = encodeURIComponent(
                            `${modalItem.businessname}\n${modalItem.businesstype || ''}\n\nLocation: ${modalItem.locationname || ''}\n${modalItem.address1 ? `Address: ${modalItem.address1}\n` : ''}Contact: ${modalItem.contactnumber || ''}\n${modalItem.emailid ? `Email: ${modalItem.emailid}\n` : ''}\n${modalItem.aboutdescription ? `\nAbout:\n${modalItem.aboutdescription}` : ''}`
                          );
                          window.location.href = `mailto:?subject=${subject}&body=${body}`;
                          setShowShareMenu(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          background: '#fff',
                          color: '#333',
                          border: 'none',
                          fontSize: 24,
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      >
                        ✉️
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Social media links */}
              {(modalItem.sm_instagram || modalItem.sm_facebook || modalItem.sm_youtube) && (
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #eee' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <strong style={{ fontSize: 14, color: '#333' }}>Connect:</strong>
                    <div style={{ display: 'flex', gap: 12 }}>
                    {modalItem.sm_instagram && (
                      <a
                        href={`https://instagram.com/${modalItem.sm_instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          borderRadius: 6,
                          background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                          color: '#fff',
                          fontSize: 17,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif',
                          transition: 'transform 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        title="Instagram"
                      >
                        IG
                      </a>
                    )}
                    {modalItem.sm_facebook && (
                      <a
                        href={`https://facebook.com/${modalItem.sm_facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          borderRadius: 6,
                          background: '#1877F2',
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif',
                          transition: 'transform 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        title="Facebook"
                      >
                        f
                      </a>
                    )}
                    {modalItem.sm_youtube && (
                      <a
                        href={`https://youtube.com/${modalItem.sm_youtube}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          borderRadius: 6,
                          background: '#FF0000',
                          color: '#fff',
                          fontSize: 16,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif',
                          transition: 'transform 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        title="YouTube"
                      >
                        ▶
                      </a>
                    )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rentals;
